// User Group Types for JavaScript
// Phase 3 - Task Management System

import { Timestamp } from 'firebase/firestore';

// ==================== USER GROUP DEFAULTS ====================

/**
 * Create default user group
 */
export const createDefaultUserGroup = () => {
  const now = Timestamp.now();
  
  return {
    // IDENTIFICATION
    id: null,
    name: '',
    description: '',
    
    // GROUP TYPE
    groupType: '',  // From nomenclature
    
    // ORGANIZATIONAL CONTEXT
    organizationId: null,
    locationId: null,
    departmentId: null,
    departmentName: null,
    buildingId: null,
    floorId: null,
    
    // MEMBERS
    members: [],
    
    // SUPERVISORS
    supervisors: [],
    
    // CHARACTERISTICS
    shiftType: null,
    requiredSkills: [],
    requiredCertifications: [],
    
    // ASSIGNMENT RULES
    assignmentRules: {
      maxConcurrentTasks: 3,
      maxDailyTasks: 12,
      workloadBalancing: 'round_robin',
      autoAssignment: true,
      requiresAcknowledgment: true
    },
    
    // NOTIFICATION SETTINGS
    notificationSettings: {
      notifyOnTaskAssignment: true,
      notifyOnUrgentTask: true,
      notifyOnOverdue: true,
      notifyOnEscalation: true,
      notificationChannels: ['app'],
      escalationDelay: 30
    },
    
    // AVAILABILITY
    schedule: {
      startTime: '07:00',
      endTime: '15:00',
      daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      holidays: false
    },
    
    // STATUS
    isActive: true,
    
    // AUDIT
    createdAt: now,
    createdBy: '',
    updatedAt: now,
    updatedBy: ''
  };
};

/**
 * Create group member
 */
export const createGroupMember = (userId, userName, role, isPrimary = true) => {
  return {
    userId,
    userName,
    role,
    isPrimary,
    isBackup: !isPrimary,
    addedAt: Timestamp.now(),
    addedBy: ''
  };
};

/**
 * Create group supervisor
 */
export const createGroupSupervisor = (userId, userName, role) => {
  return {
    userId,
    userName,
    role
  };
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Validate user group
 */
export const validateUserGroup = (group) => {
  const errors = [];
  
  if (!group.name || group.name.trim() === '') {
    errors.push('Group name is required');
  }
  
  if (!group.groupType) {
    errors.push('Group type is required');
  }
  
  if (group.members.length === 0) {
    errors.push('At least one member is required');
  }
  
  if (group.assignmentRules.maxConcurrentTasks <= 0) {
    errors.push('Max concurrent tasks must be greater than 0');
  }
  
  if (group.assignmentRules.maxDailyTasks <= 0) {
    errors.push('Max daily tasks must be greater than 0');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Get available members (not at capacity)
 */
export const getAvailableMembers = (group, currentTaskCounts) => {
  return group.members.filter(member => {
    const count = currentTaskCounts[member.userId] || 0;
    return count < group.assignmentRules.maxConcurrentTasks;
  });
};

/**
 * Get next member for round-robin assignment
 */
export const getNextRoundRobinMember = (group, lastAssignedIndex, currentTaskCounts) => {
  const availableMembers = getAvailableMembers(group, currentTaskCounts);
  
  if (availableMembers.length === 0) {
    return null;
  }
  
  // Simple round-robin: next available member after last assigned
  const nextIndex = (lastAssignedIndex + 1) % availableMembers.length;
  return availableMembers[nextIndex];
};

/**
 * Get least busy member
 */
export const getLeastBusyMember = (group, currentTaskCounts) => {
  const availableMembers = getAvailableMembers(group, currentTaskCounts);
  
  if (availableMembers.length === 0) {
    return null;
  }
  
  // Find member with fewest current tasks
  return availableMembers.reduce((least, member) => {
    const memberCount = currentTaskCounts[member.userId] || 0;
    const leastCount = currentTaskCounts[least.userId] || 0;
    return memberCount < leastCount ? member : least;
  });
};

/**
 * Check if group is available at given time
 */
export const isGroupAvailable = (group, dateTime) => {
  const date = dateTime.toDate ? dateTime.toDate() : new Date(dateTime);
  const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
  
  // Check if day is in schedule
  if (!group.schedule.daysOfWeek.includes(dayOfWeek)) {
    return false;
  }
  
  // Check if time is within schedule
  const time = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  return time >= group.schedule.startTime && time <= group.schedule.endTime;
};

/**
 * Get group statistics
 */
export const getGroupStats = (group) => {
  return {
    memberCount: group.members.length,
    primaryMembers: group.members.filter(m => m.isPrimary).length,
    backupMembers: group.members.filter(m => m.isBackup).length,
    supervisorCount: group.supervisors.length,
    isActive: group.isActive
  };
};

// ==================== UI OPTIONS ====================

export const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

export const WORKLOAD_BALANCING_OPTIONS = [
  { value: 'round_robin', label: 'Round Robin (Rotate assignments)' },
  { value: 'least_busy', label: 'Least Busy (Assign to person with fewest tasks)' },
  { value: 'skill_based', label: 'Skill-Based (Match skills to requirements)' },
  { value: 'priority_based', label: 'Priority-Based (Assign to highest priority person)' },
  { value: 'manual', label: 'Manual (Supervisor assigns)' }
];

export const NOTIFICATION_CHANNEL_OPTIONS = [
  { value: 'app', label: 'In-App Notification' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'push', label: 'Push Notification' },
  { value: 'phone', label: 'Phone Call (Emergency)' }
];


